import { useState, useEffect, createContext, useContext, ReactNode, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type AppRole = 'user' | 'admin' | 'super_admin' | 'moderator';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isModerator: boolean;
  /** True if user is admin, super_admin, or moderator - can access admin panel */
  hasAdminPanelAccess: boolean;
  userRole: AppRole | null;
  isLoading: boolean;
  /** True while role check is in progress after auth */
  isRoleLoading: boolean;
  isEmailVerified: boolean;
  authProvider: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRoleLoading, setIsRoleLoading] = useState(true); // Start as true to prevent premature access checks
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  
  // Track if a role check is currently in progress (use ref to avoid stale closures)
  const roleCheckInProgress = useRef(false);

  // Computed: can access admin panel (admin, super_admin, or moderator)
  const hasAdminPanelAccess = isAdmin || isSuperAdmin || isModerator;

  const checkUserRole = async (userId: string): Promise<{ isAdmin: boolean; isSuperAdmin: boolean; isModerator: boolean; role: AppRole | null }> => {
    try {
      // User may have multiple roles, get all and determine highest privilege
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error checking user role:', error);
        return { isAdmin: false, isSuperAdmin: false, isModerator: false, role: null };
      }
      
      if (!data || data.length === 0) {
        return { isAdmin: false, isSuperAdmin: false, isModerator: false, role: 'user' };
      }
      
      // Check for each role type
      const roles = data.map(r => r.role as AppRole);
      const isSuperAdminRole = roles.includes('super_admin');
      const isAdminRole = isSuperAdminRole || roles.includes('admin');
      const isModeratorRole = roles.includes('moderator');
      
      // Return highest role
      let highestRole: AppRole = 'user';
      if (isSuperAdminRole) highestRole = 'super_admin';
      else if (isAdminRole) highestRole = 'admin';
      else if (isModeratorRole) highestRole = 'moderator';
      
      return { isAdmin: isAdminRole, isSuperAdmin: isSuperAdminRole, isModerator: isModeratorRole, role: highestRole };
    } catch (err) {
      console.error('Failed to check user role:', err);
      return { isAdmin: false, isSuperAdmin: false, isModerator: false, role: null };
    }
  };

  const updateAuthProvider = async (userId: string, provider: string) => {
    try {
      await supabase
        .from('profiles')
        .update({ auth_provider: provider })
        .eq('user_id', userId);
    } catch (err) {
      console.error('Failed to update auth provider:', err);
    }
  };

  const getAuthProvider = async (userId: string): Promise<string | null> => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('auth_provider')
        .eq('user_id', userId)
        .maybeSingle();
      
      return data?.auth_provider || null;
    } catch (err) {
      console.error('Failed to get auth provider:', err);
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST - this is the recommended pattern
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return;
        
        // Auth events logged only in development, never log session data
        console.log('[Auth] Event:', event, 'User:', currentSession?.user?.email || 'none');
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Mark initial auth as complete
        if (event === 'INITIAL_SESSION') {
          setIsLoading(false);
        }
        
        // Check email verification status
        if (currentSession?.user) {
          const emailVerified = currentSession.user.email_confirmed_at !== null;
          setIsEmailVerified(emailVerified);
          
          // Detect if this is a Google OAuth sign-in
          const isGoogleProvider = currentSession.user.app_metadata?.provider === 'google' ||
            currentSession.user.identities?.some(i => i.provider === 'google');
          
          // Mark role as loading while we check
          roleCheckInProgress.current = true;
          setIsRoleLoading(true);
          
          // Use setTimeout to avoid potential Supabase client deadlock
          setTimeout(async () => {
            if (!mounted) return;
            
            try {
              console.log('[Auth] Starting role check for:', currentSession.user.email);
              const roleStatus = await checkUserRole(currentSession.user.id);
              console.log('[Auth] Role check result:', roleStatus);
              
              if (mounted) {
                setIsAdmin(roleStatus.isAdmin);
                setIsSuperAdmin(roleStatus.isSuperAdmin);
                setIsModerator(roleStatus.isModerator);
                setUserRole(roleStatus.role);
              }
              
              // Get and set auth provider
              const provider = await getAuthProvider(currentSession.user.id);
              if (mounted) {
                // If Google user but provider not set, update it
                if (isGoogleProvider && provider !== 'google') {
                  await updateAuthProvider(currentSession.user.id, 'google');
                  setAuthProvider('google');
                } else {
                  setAuthProvider(provider || 'email');
                }
              }
              
              // Claim daily reward on login (fires on SIGNED_IN or session refresh)
              if (event === 'SIGNED_IN') {
                try {
                  const { data } = await supabase.rpc('claim_daily_reward');
                  const result = data as { success: boolean; message: string; amount?: number } | null;
                  if (result?.success) {
                    toast.success(`🎁 Daily Reward: +${result.amount} credits!`);
                  }
                } catch {
                  // Silent fail for daily reward - non-critical
                }
                
                // Process pending referral code if exists
                const pendingRefCode = localStorage.getItem('pendingReferralCode');
                if (pendingRefCode) {
                  try {
                    const { data: refData } = await supabase.rpc('process_referral_signup', {
                      p_referral_code: pendingRefCode,
                    });
                    const refResult = refData as { success: boolean; message: string } | null;
                    if (refResult?.success) {
                      toast.success('Referral registered! Complete predictions to reward your friend.');
                    }
                    localStorage.removeItem('pendingReferralCode');
                  } catch {
                    // Silent fail for referral - non-critical
                  }
                }
              }
            } finally {
              if (mounted) {
                roleCheckInProgress.current = false;
                setIsRoleLoading(false);
                console.log('[Auth] Role loading complete');
              }
            }
          }, 0);
        } else {
          // No user session
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsModerator(false);
          setUserRole(null);
          setIsEmailVerified(false);
          setAuthProvider(null);
          roleCheckInProgress.current = false;
          setIsRoleLoading(false);
        }
      }
    );

    // Fallback: ensure loading states are set to false even if INITIAL_SESSION doesn't fire
    // But DON'T force role loading to false if a check is in progress
    const timeout = setTimeout(() => {
      if (mounted) {
        if (isLoading) {
          console.warn('[Auth] Initialization timeout - forcing loading to false');
          setIsLoading(false);
        }
        // Only force role loading to false if no check is in progress
        if (!roleCheckInProgress.current) {
          setIsRoleLoading(false);
        }
      }
    }, 5000); // Extended to 5 seconds to give role check more time

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    
    // Check if user's email is verified
    if (!error) {
      const { data: { user: signedInUser } } = await supabase.auth.getUser();
      if (signedInUser && !signedInUser.email_confirmed_at) {
        // Sign out if email not verified
        await supabase.auth.signOut();
        return { 
          error: new Error('Email not confirmed. Please check your inbox and verify your email before signing in.') 
        };
      }
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: 'https://winwgo.com' }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, isSuperAdmin, isModerator, hasAdminPanelAccess, userRole, isLoading, isRoleLoading, isEmailVerified, authProvider, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
